// Portfolio Diversification Checker -- TanStack Query hooks for the PUBLIC
// submit + poll endpoints.
//
// AUTH / BASE-URL NOTE: unauthenticated marketing endpoint, so -- exactly like
// the sibling free calculators -- we reuse apiClient's resolved baseURL via a
// clean, interceptor-free axios instance (no Bearer token, no 401 redirect).

import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useState } from 'react';

import { apiClient } from '../../../api/client';
import type {
    AnalyzeAccepted,
    CoverageResponse,
    DiversificationJobStatus,
    PortfolioRequest,
} from '../types';

const publicApiClient = axios.create({
    baseURL: apiClient.defaults.baseURL,
    timeout: 20_000,
    headers: { 'Content-Type': 'application/json' },
});

const BASE = '/portfolio-diversification-checker';
const POLL_INTERVAL_MS = 1_500;
const POLL_TIMEOUT_MS = 150_000;

const toError = (error: unknown, fallback: string): Error => {
    if (axios.isAxiosError(error)) {
        // Backend envelope (app/core/exceptions.py): { status, message, data }.
        // Validation errors carry the field errors as a list under `data`.
        const body = error.response?.data as
            | { detail?: unknown; message?: unknown; data?: unknown }
            | undefined;
        const fieldErrors = Array.isArray(body?.data) ? body?.data : body?.detail;
        if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            const first = fieldErrors[0] as { msg?: string };
            if (first?.msg) return new Error(first.msg.replace(/^Value error, /, ''));
        }
        if (typeof body?.detail === 'string') return new Error(body.detail);
        if (typeof body?.message === 'string' && error.response?.status !== 500) {
            return new Error(body.message);
        }
        return new Error(fallback);
    }
    return error instanceof Error ? error : new Error(fallback);
};

export const useAnalyzeDiversification = () =>
    useMutation<AnalyzeAccepted, Error, PortfolioRequest>({
        mutationFn: async (payload) => {
            try {
                const { data } = await publicApiClient.post<AnalyzeAccepted>(
                    `${BASE}/analyze`,
                    payload,
                );
                return data;
            } catch (error) {
                throw toError(error, 'Could not start the analysis. Please try again.');
            }
        },
    });

export const fetchDiversificationCoverage = async (tickers: string[]): Promise<CoverageResponse> => {
    const { data } = await publicApiClient.post<CoverageResponse>(`${BASE}/coverage`, { tickers });
    return data;
};

// Probes how much overlapping price history the local store holds for the
// current tickers, so the form can offer only the lookback windows we can serve.
export const useDiversificationCoverage = (tickers: string[]) =>
    useQuery<CoverageResponse>({
        queryKey: ['diversification-coverage', [...tickers].sort().join(',')],
        queryFn: () => fetchDiversificationCoverage(tickers),
        enabled: tickers.length > 0,
        staleTime: 5 * 60_000,
        retry: false,
    });

const TERMINAL: ReadonlyArray<string> = ['complete', 'failed', 'not_found'];

export const useDiversificationResults = (jobId: string | null) => {
    const [timedOutJob, setTimedOutJob] = useState<string | null>(null);
    useEffect(() => {
        if (!jobId) return undefined;
        const timer = window.setTimeout(() => setTimedOutJob(jobId), POLL_TIMEOUT_MS);
        return () => window.clearTimeout(timer);
    }, [jobId]);
    const timedOut = () => !!jobId && timedOutJob === jobId;

    const query = useQuery<DiversificationJobStatus, Error>({
        queryKey: ['diversificationResults', jobId],
        queryFn: async () => {
            try {
                const { data } = await publicApiClient.get<DiversificationJobStatus>(
                    `${BASE}/results/${encodeURIComponent(jobId as string)}`,
                );
                return data;
            } catch (error) {
                throw toError(error, 'Could not read the analysis status.');
            }
        },
        enabled: !!jobId,
        retry: 2,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchInterval: (q) => {
            const status = q.state.data?.status;
            if (status && TERMINAL.includes(status)) return false;
            if (timedOut()) return false;
            return POLL_INTERVAL_MS;
        },
    });

    const status = query.data?.status;
    const isPolling = !!jobId && !query.isError && (!status || !TERMINAL.includes(status)) && !timedOut();
    const isTimedOut = !!jobId && !query.isError && (!status || !TERMINAL.includes(status)) && timedOut();

    return { ...query, isPolling, isTimedOut };
};
